import type ProfileStore from "../../../stores/ProfileStore";
import {makeAutoObservable, toJS} from "mobx";
import type {CategorizationReviewPageController} from "./CategorizationReviewPageController";
import {randomId} from "~/utils/js-utils.ts";
import type {GridFilterItem, GridFilterModel} from "@mui/x-data-grid-premium";
import {
    GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
    type GridCallbackDetails,
    GridLogicOperator
} from "@mui/x-data-grid-premium";
import type {AiChangeChart} from "~/services/classes/CategorizationHelpers.ts";
import {convertToChart} from "~/services/classes/CategorizationHelpers.ts";
import type {MatReviewLevelStatisticsTreeSerializer} from "~/services/classes/MaterializedClasses.ts";
import type {BarDataPoint} from "~/components/visualization/BarChart.tsx";
import type {ByPartIntent} from "../classes/CategorizationReviewUrls.ts";
import {UNCATEGORIZED_VALUE} from "~/constants.ts";
import {GROUP_KEY, GROUP_VAL} from "../components/review-table/review-table-muix/CategorizationTableDataModel.ts";
import type {BusinessUnitManager} from "~/stores/managers/BusinessUnitManager.ts";
import {showBusinessUnit} from "~/stores/managers/BusinessUnitManager.ts";
import {strTrim} from "~/utils/str.utils.ts";
import {
    muiXItemMatches,
    removeMuixFilterItem,
    setMuixFilterItem,
    toggleMuixFilterItem,
    unsureMuixFilterValue
} from "~/utils/muixUtils.ts";
import {
    CAT_EQ_OPERATOR,
    CAT_NEQ_OPERATOR
} from "../components/review-table/review-table-muix/filters/CategoryFilter.tsx";

export const AI_CATEGORIZATION_SCORE_FACTOR = 100;
export const AI_CATEGORIZATION_SCORE_DIGITS = 0;
const AI_CATEGORIZATION_ROUNDING_ERROR_CORRECTION = .5 / AI_CATEGORIZATION_SCORE_FACTOR;
const AI_CATEGORIZATION_SCORE_INITIAL_MIN = 0.5;
const AI_CATEGORIZATION_SCORE_INITIAL_MAX = 1.0;

type AdvancedFilterTypes = {
    scoreLow: number | null
    scoreHigh: number | null

    byYear: number | null

    isReviewed: boolean | null
    isRecategorized: boolean | null
}

const FILTER_STORAGE_PREFIX = 'm_review.filter.v1'
const STORAGE_KEY_ENABLED = `${FILTER_STORAGE_PREFIX}.enabled`
const STORAGE_KEY_VALUE = `${FILTER_STORAGE_PREFIX}.value`

const EMPTY_FILTER_MODEL: GridFilterModel = {
    items: [],
    logicOperator: GridLogicOperator.And,
    quickFilterValues: [],
    quickFilterLogicOperator: GridLogicOperator.And,
}

const VALID_VALUE = '1';

const GROUP_FILTER_ID = 'group_999';
const CATEGORY_BAR_FILTER_ID = 'category_999';
const PREVIOUS_CATEGORY_FILTER_ID = 'category_prev_999';
const CATEGORY_JOIN_STRING = ' > ';
export const CATEGORY_FILTER_FIELD = 'cat';
const CATEGORY_FILTER_KEY: GridFilterItem = {
    field: CATEGORY_FILTER_FIELD,
    operator: 'equals',
}
export const PREVIOUS_CATEGORY_FILTER_FIELD = 'previous_cat';
const PREVIOUS_CATEGORY_FILTER_KEY: GridFilterItem = {
    field: PREVIOUS_CATEGORY_FILTER_FIELD,
    operator: 'equals',
}

// export const CATEGORY_PREV_EQ_FILTER_VALUE_SAME = 'same';
// export const CATEGORY_PREV_EQ_FILTER_VALUE_DIFFERENT = 'different';

const BUSINESS_UNIT_FILTER_ID = 'bu_999';
/**
 * Hidden field to ensure we can integrate the external BU filter
 */
export const BUSINESS_UNIT_FILTER_FIELD = '__business_unit_id__';

export const BUSINESS_UNIT_VALUE_FIELD = 'bu_name';

export const REVIEW_CHOICE_FIELD = 'review_choice';

/**
 * These fields will trigger the external BU filter to be disabled
 */
const BUSINESS_UNIT_FILTER_FIELDS = ['bu_id', BUSINESS_UNIT_VALUE_FIELD, BUSINESS_UNIT_FILTER_FIELD];

/**
 * The filter is an observable
 *
 * It's updated with actions and is read from in observables (as much as possible)
 * changes are automatically propagated to the DataGrid component in CategorizationReviewTableMuiX
 */
export class CategorizationReviewPageFilterController {
    /**
     * The selected category
     */
    selectedCategory: string[] = [];
    selectedPreviousCategory: string[] = [];

    /**
     * The active search for groups and s_name, could be a series of items if filter toolbar is used
     */
    groupSearch: Array<string> = [];

    _muiXGridFilterModel: GridFilterModel = EMPTY_FILTER_MODEL;

    constructor(
        private readonly controller: CategorizationReviewPageController,
        private readonly profile: ProfileStore,
        private readonly buManager: BusinessUnitManager,
    ) {
        makeAutoObservable(this);
    }

    clearFilters() {
        this.selectedCategory = [];
        this.selectedPreviousCategory = [];
        this.groupSearch = [];
        this.buManager.setNoneSelected();
        this._muiXGridFilterModel = EMPTY_FILTER_MODEL;
        // TODO:
        // this.storePersistentStorage()
        // this.categorizationReviewPageController.loadData(false, true)
        this.controller.reviewPageTableController.resetPagination();
        return true;
    }

    onApplyGroupSearch(item: GridFilterItem, value: string) {
        this.groupSearch = [value.trim()];
        this.controller.reviewPageTableController.resetPagination();
    }

    setSupplierSearch(value: string) {
        this.groupSearch = [value.trim()];
        this.controller.reviewPageTableController.resetPagination();
    }

    get supplierFilterValue(): string {
        return this.groupSearch.filter(i => i)?.at(0) ?? '';
    }

    get selectedBusinessUnitId(): number | null | undefined {
        if (this.buManager.selectedBusinessUnit === undefined) return undefined;
        if (this.buManager.selectedBusinessUnit === null) return null;
        return this.buManager.selectedBusinessUnit.id;
    }

    get selectedBusinessUnitName(): string {
        const b = this.buManager.selectedBusinessUnit;
        if (b === null) return 'not set';
        if (!b) return 'any';
        return showBusinessUnit(b)
    }

    onFilterModelChange(model: GridFilterModel, details: GridCallbackDetails<'filter'>): void {
        console.debug('onFilterModelChange', details.reason, toJS(model));
        let items = [...model.items];
        let groupFilterIndex = -1;
        this.groupSearch = [];
        do {
            groupFilterIndex = this.controller.isGroupBy
                ? items.findIndex(i => i.field === GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD || i.field === GROUP_KEY)
                : items.findIndex(i => i.field === GROUP_VAL);
            if (groupFilterIndex >= 0) {
                this.groupSearch.push(items[groupFilterIndex].value ? String(items[groupFilterIndex].value) : '');
                items.splice(groupFilterIndex, 1);
            }
        } while (groupFilterIndex >= 0);
        // Extract bu
        const buFilterIndex = items.findIndex(i => BUSINESS_UNIT_FILTER_FIELDS.includes(i.field) && String(i.id) !== BUSINESS_UNIT_FILTER_ID && !!i.value);
        if (buFilterIndex >= 0) {
            // A Business Unit filter is applied (via some other filter component)
            this.buManager.setNoneSelected();
        }
        // Extract category filter
        const categoryFilterIndex = items.findIndex(i => i.id === CATEGORY_BAR_FILTER_ID);
        if (categoryFilterIndex >= 0) {
            this.setSelectedCategory(items[categoryFilterIndex].value.split(CATEGORY_JOIN_STRING));
            items.splice(categoryFilterIndex, 1);
        }
        const previousCategoryFilterIndex = items.findIndex(i => i.id === PREVIOUS_CATEGORY_FILTER_ID);
        if (previousCategoryFilterIndex >= 0) {
            this.setSelectedPreviousCategory(items[previousCategoryFilterIndex].value.split(CATEGORY_JOIN_STRING));
            items.splice(previousCategoryFilterIndex, 1);
        }
        if (items.filter(i => i.id !== CATEGORY_BAR_FILTER_ID && muiXItemMatches(i, CATEGORY_FILTER_KEY)).length > 0) {
            if (categoryFilterIndex >= 0)
                console.error(`Category filter is being overwritten ${toJS(this.selectedCategory)}`);
            this.setSelectedCategory(items.filter(
                i => i.id !== CATEGORY_BAR_FILTER_ID && muiXItemMatches(i, CATEGORY_FILTER_KEY)
            ).sort(
                (a, b) => Number(Array.from(a.field).reverse()[0]) - Number(Array.from(b.field).reverse()[0])
            ).map(
                i => i.value
            ));
            items = items.filter(i => !muiXItemMatches(i, CATEGORY_FILTER_KEY));
        }
        if (items.filter(i => i.id !== CATEGORY_BAR_FILTER_ID && muiXItemMatches(i, PREVIOUS_CATEGORY_FILTER_KEY)).length > 0) {
            if (previousCategoryFilterIndex >= 0)
                console.error(`Previous Category filter is being overwritten ${toJS(this.selectedPreviousCategory)}`);
            this.setSelectedCategory(items.filter(
                i => i.id !== CATEGORY_BAR_FILTER_ID && muiXItemMatches(i, PREVIOUS_CATEGORY_FILTER_KEY)
            ).sort(
                (a, b) => Number(Array.from(a.field).reverse()[0]) - Number(Array.from(b.field).reverse()[0])
            ).map(
                i => i.value
            ));
            items = items.filter(i => !muiXItemMatches(i, PREVIOUS_CATEGORY_FILTER_KEY));
        }

        // Observables can only be changed in actions, so do any modifications here
        items.forEach((i, idx) => {
            if (i.operator === CAT_EQ_OPERATOR.operatorValue || i.operator === CAT_NEQ_OPERATOR.operatorValue) {
                i.value = VALID_VALUE; // Must be set to act like a normal filter item
            }
            // Ids could be messed up. Consider the following scenario:
            //  1. User adds a filter (defaults to Supplier)
            //  2. We store that filter in groupSearch and assign an id to it "group_999_n"
            //  3. User changes the field to p_id (if we don't change id, it would still be "group_999_n"
            //  4. User adds another filter for supplier. Now items would have two fields with same id "group_999_n"
            i.id = i.field + '_' + String(idx);
        });
        this.controller.reviewPageTableController.resetPagination();
        this._muiXGridFilterModel = {...model, items};
    }

    /**
     * The combined filter model for the DataGrid component
     */
    get gridFilterModel(): GridFilterModel {
        let items = [...this._muiXGridFilterModel.items];

        if (this.selectedCategory.length !== 0) {
            // Ensure the CATEGORY_FILTER_KEY is also set
            const value = this.selectedCategory.join(CATEGORY_JOIN_STRING); // Only shown in the UI
            unsureMuixFilterValue(items, CATEGORY_FILTER_KEY, value, CATEGORY_BAR_FILTER_ID)
        }
        if (this.selectedPreviousCategory.length !== 0) {
            const value = this.selectedPreviousCategory.join(CATEGORY_JOIN_STRING); // Only shown in the UI
            unsureMuixFilterValue(items, PREVIOUS_CATEGORY_FILTER_KEY, value, PREVIOUS_CATEGORY_FILTER_ID)
        }

        // For the group search, we have to switch the fields based on Group-by or not
        items = items.filter(i => i.field !== GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD && i.field !== GROUP_VAL && i.field !== GROUP_KEY);
        items = items.concat(this.groupSearch.map((gs, idx) => ({
            id: GROUP_FILTER_ID + '_' + String(idx),
            field: this.controller.isGroupBy ? GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD : GROUP_VAL,
            operator: 'contains',
            value: gs,
        })));

        items = items.filter(i => i.id !== BUSINESS_UNIT_FILTER_ID);
        if (this.buManager.selectedBusinessUnit !== undefined) {
            items.push({
                id: BUSINESS_UNIT_FILTER_ID,
                field: BUSINESS_UNIT_FILTER_FIELD,
                operator: this.buManager.selectedBusinessUnit === null ? 'isEmpty' : 'equals',
                value: this.buManager.selectedBusinessUnit?.bu_name || '',
            })
        }

        return {...this._muiXGridFilterModel, items};
    }

    /**
     * The filters (whereby the group field is replaced with GROUP_VAL)
     */
    get apiGridFilterModel(): GridFilterModel {
        let items = [...this._muiXGridFilterModel.items];
        // For the selected category, multiple filters are used, one for each level
        const apiCatFilterItems = this.selectedCategory.map((label, index) => ({
            id: `cat_l${index + 1}`, // Ids don't actually matter for the API
            field: 'l' + String(index + 1),
            operator: 'equals',
            value: label,
        }))
        setMuixFilterItem(items, CATEGORY_FILTER_KEY, this.selectedCategory.length !== 0, ...apiCatFilterItems)
        // Previous
        const apiPrevCatFilterItems = this.selectedPreviousCategory.map((label, index) => ({
            id: `previous_cat__l${index + 1}`, // Ids don't actually matter for the API
            field: 'previous_cat__l' + String(index + 1),
            operator: 'equals',
            value: label,
        }))
        setMuixFilterItem(items, PREVIOUS_CATEGORY_FILTER_KEY, this.selectedPreviousCategory.length !== 0, ...apiPrevCatFilterItems)
        // For the group search, we have to switch the fields based on Group-by or not
        items = items.filter(i => i.field !== GROUP_VAL && i.field !== GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD && i.field !== GROUP_KEY);
        items = items.concat(this.groupSearch.map((gs, idx) => ({
            id: GROUP_FILTER_ID + '_' + String(idx),
            // Note that API call must use a field known to BE (in this case GROUP_VAL = s_name)
            field: GROUP_VAL,
            operator: 'contains',
            value: gs,
        })));
        // BU
        items = items.filter(i => i.id !== BUSINESS_UNIT_FILTER_ID);
        if (this.buManager.selectedBusinessUnit !== undefined) {
            items.push({
                id: BUSINESS_UNIT_FILTER_ID,
                field: strTrim(BUSINESS_UNIT_FILTER_FIELD, '__'),
                operator: this.buManager.selectedBusinessUnit === null ? 'isEmpty' : 'equals',
                value: this.selectedBusinessUnitId,
            })
        }
        // Score
        const score_filter_item = items.find(i => i.field === 'suggestion_score');
        if (score_filter_item && Array.isArray(score_filter_item["value"]) && score_filter_item["value"].length === 2) {
            items = items.filter(i => i.field !== 'suggestion_score')
            items.push({
                ...score_filter_item,
                value: [
                    (Number(score_filter_item["value"][0] ?? '0') / 100).toFixed(2),
                    (Number(score_filter_item["value"][1] ?? '100') / 100).toFixed(2)
                ]
            })
        }
        return {...this._muiXGridFilterModel, items};
    }

    removeFilter(id: number | string | undefined) {
        if (!id) {
            console.warn('CategorizationReviewPageFilterController.removeFilter: id is undefined');
            return;
        }
        const item = this.gridFilterModel.items.find(i => i.id === id);
        if (muiXItemMatches(item, CATEGORY_FILTER_KEY)) {
            this.setSelectedCategory([]);
            this.controller.reviewPageTableController.resetPagination();
            return;
        }
        if (muiXItemMatches(item, PREVIOUS_CATEGORY_FILTER_KEY)) {
            this.setSelectedPreviousCategory([]);
            this.controller.reviewPageTableController.resetPagination();
            return;
        }
        if (typeof id === 'string' && id.startsWith(GROUP_FILTER_ID)) {
            this.groupSearch = [];
            this.controller.reviewPageTableController.resetPagination();
            return;
        }
        if (id === BUSINESS_UNIT_FILTER_ID) {
            this.buManager.setNoneSelected();
            this.controller.reviewPageTableController.resetPagination();
            return;
        }
        const i = this._muiXGridFilterModel.items.findIndex(item => item.id === id);
        if (i !== -1) {
            this._muiXGridFilterModel.items.splice(i, 1);
            this.controller.reviewPageTableController.resetPagination();
            return;
        }
        console.warn('CategorizationReviewPageFilterController.removeFilter: filter not found', {id})
    }

    get selectedLevel(): number {
        return this.selectedCategory.length;
    }

    get currentSelectionStats(): undefined | MatReviewLevelStatisticsTreeSerializer[] {
        if (!this.hasRemainingSelectionLevels) return undefined;
        if (!this.controller._selectedStats) return undefined;
        return this.controller._selectedStats[this.controller._selectedStats.length - 1].children;
    }

    get selectionCharts(): {data: AiChangeChart[], max: number} | undefined {
        if (!this.hasRemainingSelectionLevels) return undefined;
        const d = this.currentSelectionStats;
        if (!d) return undefined;

        const data = convertToChart(d, this.profile.currencySymbol)

        if (this.profile.p.hackHideUncategorizedInReview) {
            const uncategorizedIndex = data.findIndex(d => d.category === UNCATEGORIZED_VALUE)
            if (uncategorizedIndex !== -1) {
                console.warn('Hack: Hiding uncategorized in review')
                data.splice(uncategorizedIndex, 1)
            }
        }

        if (this.profile.p.allowIncompleteTaxonomy && this.selectedLevel > 0) {
            // Remove single uncategorized leafs
            if (data.length === 1 && data[0].category === UNCATEGORIZED_VALUE) {
                return undefined;
            }
        }

        const COL_WIDTH = 6 / 9 // Show as width col=6, while the whole viz goes to col=9
        const max = Math.max(...data.map(D => Math.max(...D.values.map(v => v.value)))) / COL_WIDTH;
        return {data, max}
    }

    /**
     * @deprecated Still needed?
     */
    handleDistributionClick(d: BarDataPoint) {
        if (d.category === "0_20") {
            this.clickAdvancedFilter(0, 20)
        }
        if (d.category === "20_40") {
            this.clickAdvancedFilter(21, 40)
        }
        if (d.category === "40_60") {
            this.clickAdvancedFilter(41, 60)
        }
        if (d.category === "60_80") {
            this.clickAdvancedFilter(61, 80)
        }
        if (d.category === "80_100") {
            this.clickAdvancedFilter(81, 100)
        }
        this.controller.reviewPageTableController.resetPagination();
    }

    /**
     * @deprecated Still needed?
     */
    clickAdvancedFilter(lowValue: number, highValue: number) {
        throw new Error('Not implemented')
    }

    setSearch(colum: ByPartIntent['part_search_fields'], value: string) {
        throw new Error('Not implemented');
    }

    setSelectedCategory(selectedCategory: string[]) {
        this.selectedCategory = selectedCategory;
        this.controller.reviewPageTableController.resetPagination();
    }

    setSelectedPreviousCategory(selectedCategory: string[]) {
        this.selectedPreviousCategory = selectedCategory;
        this.controller.reviewPageTableController.resetPagination();
    }

    selectOneCategoryUp() {
        this.setSelectedCategory(this.selectedCategory.slice(0, -1));
        this.controller.reviewPageTableController.resetPagination();
    }

    selectPreviousOneCategoryUp() {
        this.setSelectedPreviousCategory(this.selectedPreviousCategory.slice(0, -1));
        this.controller.reviewPageTableController.resetPagination();
    }

    get effectiveBarCategoryFilter(): string[] {
        if (this.selectedCategory.length === 0) return this.selectedPreviousCategory;
        return this.selectedCategory;
    }

    selectNextCategoryDown(selectedCategory: string, dataClass: string) {
        // Since selectedPreviousCategory is filterable by bars, we have to take special considerations here
        // If selectedCategory has some values, according to bar filtering logic
        // (see _selectedStats() getter in this.controller) we have to assume that previous should be following current
        if (dataClass === 'old')
            this.setSelectedPreviousCategory([...this.effectiveBarCategoryFilter, selectedCategory]);
        else
            this.setSelectedCategory([...this.effectiveBarCategoryFilter, selectedCategory]);
    }

    navigateToLevel(level: number) {
        this.setSelectedCategory(this.selectedCategory.slice(0, level));
    }

    navigateToPreviousLevel(level: number) {
        this.setSelectedPreviousCategory(this.selectedPreviousCategory.slice(0, level));
    }

    get hasRemainingSelectionLevels(): boolean {
        if (this.selectedLevel >= this.controller.taxonomySize) {
            return false
        }
        // noinspection RedundantIfStatementJS
        if (this.selectedLevel >= 1 && !this.selectedCategoryLabel) {
            // Do not allow to go deeper in uncategorized
            return false;
        }

        return true;
    }

    get selectedCategoryLabel(): string | undefined {
        if (this.selectedCategory.length > 0) {
            return this.selectedCategory[this.selectedCategory.length - 1]
        }
        return undefined;
    }

    get selectedL1Category(): string | undefined {
        if (this.selectedCategory.length > 0) {
            return this.selectedCategory[0]
        }
        return undefined;
    }

    hasFilter(field: string, operator: string, value: GridFilterItem['value']) {
        return this._muiXGridFilterModel.items.some(i => i.field === field && i.operator === operator && JSON.stringify(i.value) == JSON.stringify(value));
    }

    /**
     * Add or Set a filter based on the field
     */
    toggleFilter(filters: Array<{field: string, operator: string, value: GridFilterItem['value']}>) {
        const filterItems = [...this._muiXGridFilterModel.items];
        filters.forEach(filter => {
            const i = filterItems.findIndex(i => i.field === filter.field);
            if (i === -1) {
                filterItems.push({
                    id: randomId(6),
                    field: filter.field,
                    operator: filter.operator,
                    value: filter.value,
                })
            } else {
                const item = filterItems[i];
                if (item.value && item.operator === filter.operator && JSON.stringify(item.value) === JSON.stringify(filter.value)) {
                    // Turn it off (but keep the ID)
                    filterItems[i] = {
                        ...item,
                        operator: filter.operator,
                        value: undefined,
                    }
                } else {
                    filterItems[i] = {
                        ...item,
                        operator: filter.operator,
                        value: filter.value,
                    }
                }
            }
        });
        this._muiXGridFilterModel.items = filterItems;
        this.controller.reviewPageTableController.resetPagination();
    }

    toggleCatEqualsPrevFilter() {
        const items = [...this._muiXGridFilterModel.items];
        const added = toggleMuixFilterItem(items, {
            field: CATEGORY_FILTER_FIELD,
            operator: CAT_EQ_OPERATOR.operatorValue,
        }, VALID_VALUE, PREVIOUS_CATEGORY_FILTER_ID);
        if (added) {
            removeMuixFilterItem(items, {
                field: CATEGORY_FILTER_FIELD,
                operator: CAT_NEQ_OPERATOR.operatorValue,
            })
        }
        this._muiXGridFilterModel.items = items;
        this.controller.reviewPageTableController.resetPagination();
    }

    get catEqualPrevFilterIsActive() {
        return this.hasFilter(CATEGORY_FILTER_FIELD, CAT_EQ_OPERATOR.operatorValue, VALID_VALUE);
    }

    toggleCatNotEqualsPrevFilter() {
        const items = [...this._muiXGridFilterModel.items];
        const added = toggleMuixFilterItem(items, {
            field: CATEGORY_FILTER_FIELD,
            operator: CAT_NEQ_OPERATOR.operatorValue
        }, VALID_VALUE, PREVIOUS_CATEGORY_FILTER_ID);
        if (added) {
            removeMuixFilterItem(items, {
                field: CATEGORY_FILTER_FIELD,
                operator: CAT_EQ_OPERATOR.operatorValue,
            })
        }
        this._muiXGridFilterModel.items = items;
        this.controller.reviewPageTableController.resetPagination();
    }

    get catNotEqualPrevFilterIsActive() {
        return this.hasFilter(CATEGORY_FILTER_FIELD, CAT_NEQ_OPERATOR.operatorValue, VALID_VALUE);
    }
}
